import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Button } from '@naf-simba/elements-buttons';
import { useNavigate } from 'react-router';
import { toCurrentSiteUrl } from '@lib/siteHosts';
import { useDocumentUrlWithNode } from '@hooks/Documents/useDocumentUrl';
import { ValidationType, ValidationMessage } from '@appTypes/inputWithImageLinkType';
import { LinkType } from '@appTypes/LinkType';
import * as S from './InputBlock.styles';

type InputProps = {
  link: LinkType;
  label: string | undefined;
  validation: ValidationType;
  key: string;
};

export const InputBlock = ({ item: { link, label, validation, key } }: { item: InputProps }) => {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(true);
  const [inputText, setInputText] = useState<string | undefined>(undefined);

  const inputRef = useRef<HTMLInputElement>(null);

  const href = useDocumentUrlWithNode(link.internalReference);

  const validateInput = useCallback(() => {
    if (!validation || validation === ValidationType.noValidation) setIsValid(true);
    switch (validation) {
      case ValidationType.postalCode:
        if (errorMessage !== ValidationMessage.postalCode) setErrorMessage(ValidationMessage.postalCode);
        if (inputText !== undefined) setIsValid(/^\d{4}$/.test(inputText));
        break;
      default:
        break;
    }
  }, [errorMessage, inputText, validation]);

  const handleSubmit = () => {
    if (isValid && inputText) {
      setShowErrorMessage(false);
      if (href) {
        const params =
          validation &&
          Object.values<string>(ValidationType).includes(validation) &&
          validation !== ValidationType.noValidation
            ? `?${ValidationType[validation]}=${inputText}`
            : `?value=${inputText}`;
        navigate({ pathname: href, search: params });
        return;
      }
      if (link.externalTarget?.href) {
        const url =
          validation &&
          Object.values<string>(ValidationType).includes(validation) &&
          validation !== ValidationType.noValidation
            ? `${link.externalTarget?.href}?${ValidationType[validation]}=${inputText}`
            : `${link.externalTarget.href}?value=${inputText}`;
        if (link.externalTarget?.blank) window.open(toCurrentSiteUrl(url));
        else window.location.href = toCurrentSiteUrl(url);
      }
    } else {
      setShowErrorMessage(true);
    }
  };

  useEffect(() => {
    // Clear error message, will only show on button submit
    if (showErrorMessage) setShowErrorMessage(false);
    if (inputText !== undefined) validateInput();
  }, [inputText, showErrorMessage, validateInput]);

  return (
    <S.InputBlockWrapper>
      {label && <S.StyledLabel htmlFor={`inputFieldImageBlock${key}`}>{label}</S.StyledLabel>}
      <S.InputAreaWrapper>
        <S.StyledInput
          ref={inputRef}
          id={`inputFieldImageBlock${key}`}
          value={inputText}
          onChange={(value: string) => setInputText(value)}
          error={inputText ? !isValid : false}
        />
        <Button type="button" onClick={() => handleSubmit()} color="secondary">
          {link.title}
        </Button>
      </S.InputAreaWrapper>
      {showErrorMessage && <S.StyledMessage error>{errorMessage}</S.StyledMessage>}
    </S.InputBlockWrapper>
  );
};
